import React, { useEffect, useState } from 'react';
import { getInvitations, resendInvitation, revokeInvitation } from '../services/api';
import ConfirmDialog from './ConfirmDialog';

const STATUS_STYLE = {
  pending:  { background: 'var(--color-warning-dim)',  color: 'var(--color-warning)' },
  accepted: { background: 'var(--color-success-dim)',  color: 'var(--color-success)' },
  expired:  { background: 'var(--bg-input)', color: 'var(--text-faint)' },
  revoked:  { background: 'var(--color-danger-dim)',   color: 'var(--color-danger)' },
};

const ROLE_STYLE = {
  admin:     { background: 'var(--color-info-dim)', color: 'var(--color-info)' },
  evaluator: { background: 'var(--color-info-dim)', color: 'var(--color-info)' },
  member:    { background: 'var(--bg-input)', color: 'var(--text-faint)' },
};

const BADGE = { padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' };

export default function InvitesList({ refreshTrigger = 0 }) {
  const [invitations, setInvitations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null);

  useEffect(() => {
    loadInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, refreshTrigger]);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await getInvitations(params);
      if (res?.invitations) setInvitations(res.invitations);
    } catch (err) {
      console.error('[InvitesList] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (invitationId) => {
    setMessage(null);
    try {
      await resendInvitation(invitationId);
      setMessage({ type: 'success', text: 'Invitation resent successfully!' });
      loadInvitations();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to resend invitation' });
    }
  };

  const handleRevoke = (invitationId) => {
    setPendingConfirm({
      message: 'Revoke this invitation?',
      confirmLabel: 'Revoke',
      variant: 'danger',
      onConfirm: async () => {
        setMessage(null);
        try {
          await revokeInvitation(invitationId);
          loadInvitations();
        } catch (err) {
          setMessage({ type: 'error', text: err.message || 'Failed to revoke invitation' });
        }
      },
    });
  };

  const pendingCount = invitations.filter((inv) => inv.status === 'pending').length;

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(Number(timestamp)).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const daysLeft = (Number(expiresAt) - Date.now()) / (24 * 60 * 60 * 1000);
    return daysLeft > 0 && daysLeft < 7;
  };

  const FILTERS = ['all', 'pending', 'accepted', 'expired'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {message && (
        <div style={{
          padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
          background: message.type === 'success' ? 'var(--color-success-dim)' : 'var(--color-danger-dim)',
          color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
          border: `1px solid ${message.type === 'success' ? 'var(--color-success-border)' : 'var(--color-danger-border)'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Header with filter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: 'var(--text-faint)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Invitations
          {pendingCount > 0 && (
            <span style={{ marginLeft: '8px', ...BADGE, ...STATUS_STYLE.pending }}>
              {pendingCount} pending
            </span>
          )}
        </span>

        <div style={{ display: 'flex', gap: '4px' }}>
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '4px 10px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer',
                background: filter === f ? 'var(--accent-dim)' : 'transparent',
                color: filter === f ? 'var(--accent)' : 'var(--text-faint)',
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-faint)', fontSize: '13px' }}>
          Loading invitations…
        </div>
      ) : invitations.length === 0 ? (
        <div style={{ background: 'var(--bg-input)', borderRadius: '10px', padding: '24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-faint)', fontSize: '13px', margin: 0 }}>No invitations found.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {invitations.map((inv) => (
            <div
              key={inv.invitation_id}
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-accent)',
                borderRadius: '10px',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500' }}>{inv.email}</span>
                  <span style={{ ...BADGE, ...(STATUS_STYLE[inv.status] || STATUS_STYLE.expired) }}>{inv.status}</span>
                  <span style={{ ...BADGE, ...(ROLE_STYLE[inv.role] || ROLE_STYLE.member) }}>{inv.role}</span>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '11px', margin: '3px 0 0' }}>
                  Invited by {inv.inviter_name} on {formatDate(inv.created_at)}
                  {inv.status === 'pending' && (
                    <span style={isExpiringSoon(inv.expires_at) ? { color: 'var(--accent)' } : {}}>
                      {' '}· Expires {formatDate(inv.expires_at)}
                    </span>
                  )}
                  {inv.status === 'accepted' && inv.accepted_at && (
                    <span style={{ color: 'var(--color-success)' }}> · Accepted {formatDate(inv.accepted_at)}</span>
                  )}
                </p>
              </div>

              {inv.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleResend(inv.invitation_id)}
                    style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--color-info-border)', background: 'transparent', color: 'var(--color-info)', cursor: 'pointer' }}
                  >
                    Resend
                  </button>
                  <button
                    onClick={() => handleRevoke(inv.invitation_id)}
                    style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--color-danger-border)', background: 'transparent', color: 'var(--color-danger)', cursor: 'pointer' }}
                  >
                    Revoke
                  </button>
                </div>
              )}

              {(inv.status === 'expired' || inv.status === 'revoked') && (
                <button
                  onClick={() => handleResend(inv.invitation_id)}
                  style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--color-info-border)', background: 'transparent', color: 'var(--color-info)', cursor: 'pointer' }}
                >
                  Resend
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          variant={pendingConfirm.variant}
          onConfirm={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}
